import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Header } from "./shared";
import { useSettings } from "@/context/SettingsContext";
import { Archive, CalendarPlus, Warning, Trophy } from "@phosphor-icons/react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");

export default function SeasonsTab() {
  const { settings, reloadSettings } = useSettings();
  const currentTitle = settings?.season_title || "Season 2026";
  const [newTitle, setNewTitle] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [seasons, setSeasons] = useState([]);

  const loadSeasons = () => api.get("/seasons").then((r) => setSeasons(r.data || [])).catch(() => {});
  useEffect(() => { loadSeasons(); }, []);

  const [renameVal, setRenameVal] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  useEffect(() => { setRenameVal(currentTitle); }, [currentTitle]);

  const saveRename = async () => {
    const t = renameVal.trim();
    if (!t) { toast.error("Enter a season name"); return; }
    setSavingRename(true);
    try {
      await api.put("/settings", { season_title: t });
      await reloadSettings();
      toast.success("Season name updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingRename(false); }
  };

  const canSubmit = newTitle.trim() && confirmText.trim() === currentTitle && !busy;

  const startNewSeason = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await api.post("/seasons/new", { title: newTitle.trim() });
      toast.success(`New season "${r.data.season_title}" started — "${r.data.archived_title}" archived`);
      setNewTitle(""); setConfirmText("");
      await reloadSettings();
      loadSeasons();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="seasons-tab">
      <Header title="Seasons" />

      <div className="border border-white/10 bg-[#121212] p-6 mb-8 max-w-2xl" data-testid="rename-season-panel">
        <h3 className="font-display text-xl uppercase mb-1">Current Season Name</h3>
        <p className="text-white/40 text-xs mb-4">Shown across the public site. Edit it anytime — this does not reset any data.</p>
        <div className="flex gap-3 flex-wrap">
          <input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} data-testid="rename-season-input"
            className="flex-1 min-w-[200px] bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722]" />
          <button onClick={saveRename} disabled={savingRename || renameVal.trim() === currentTitle} data-testid="rename-season-btn"
            className="bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-40 text-black font-bold uppercase tracking-wide px-6 py-2.5 text-sm transition-all duration-200">
            {savingRename ? "Saving…" : "Save Name"}
          </button>
        </div>
      </div>

      <div className="border border-white/10 bg-[#121212] p-6 mb-8 max-w-2xl" data-testid="new-season-panel">
        <div className="flex items-center gap-2 mb-1"><CalendarPlus size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Start a New Season</h3></div>
        <p className="text-white/50 text-sm mb-1">Current season: <span className="text-white font-bold">{currentTitle}</span></p>
        <p className="text-white/40 text-xs mb-5">Starting a new season archives the current one (viewable under Past Seasons) and gives you a clean slate.</p>

        <div className="border border-[#FFB300]/40 bg-[#FFB300]/5 p-4 mb-5">
          <p className="flex items-center gap-2 text-[#FFB300] font-bold uppercase text-xs tracking-wide mb-2"><Warning size={16} weight="fill" /> What happens</p>
          <ul className="text-white/60 text-xs space-y-1 list-disc pl-5">
            <li><span className="text-white/80">Kept:</span> all teams and players</li>
            <li><span className="text-white/80">Reset:</span> games, standings, stats, Player of the Game &amp; playoff brackets</li>
            <li>Every player must <span className="text-white/80">re-sign their waiver</span> for the new season</li>
            <li>Current highlights are <span className="text-white/80">archived</span> with the finished season</li>
            <li>The finished season is saved under <span className="text-white/80">Past Seasons</span> (standings, champions, leaders &amp; highlights)</li>
          </ul>
        </div>

        <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">New season name</label>
        <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. Season 2027" data-testid="new-season-title-input"
          className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#FF5722]" />

        <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Type <span className="text-[#FF5722] normal-case">{currentTitle}</span> to confirm</label>
        <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={currentTitle} data-testid="new-season-confirm-input"
          className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-[#FF5722]" />

        <button onClick={startNewSeason} disabled={!canSubmit} data-testid="start-new-season-btn"
          className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold uppercase tracking-wide py-3 transition-all duration-200">
          {busy ? "Starting…" : "Start New Season"}
        </button>
      </div>

      <div data-testid="past-seasons-admin">
        <div className="flex items-center gap-2 mb-3"><Archive size={18} className="text-[#FF5722]" /><h3 className="font-display text-xl uppercase">Past Seasons</h3></div>
        {seasons.length === 0 ? (
          <p className="text-white/40 text-sm">No archived seasons yet. When you start a new season, the finished one appears here and on the public Past Seasons page.</p>
        ) : (
          <div className="space-y-2">
            {seasons.map((s) => (
              <div key={s.id} data-testid={`past-season-${s.id}`} className="flex items-center gap-4 border border-white/10 bg-[#121212] p-4">
                <div className="min-w-0 flex-1">
                  <p className="font-display text-xl uppercase truncate">{s.title}</p>
                  <p className="text-xs text-white/40">Ended {fmtDate(s.ended_at)} · {s.counts?.games_completed || 0} games · {s.counts?.players || 0} players</p>
                </div>
                {Object.keys(s.champions || {}).length > 0 && (
                  <div className="hidden sm:flex items-center gap-2 text-xs text-white/60">
                    <Trophy size={14} weight="fill" className="text-[#FF5722]" />
                    {Object.entries(s.champions).map(([div, name]) => <span key={div} className="whitespace-nowrap">{name} <span className="text-white/30">({div})</span></span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
